<template>
  <div>
    <vx-card class="mb-8">
      <!-- Upload area with dropzone -->
      <div
        class="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-6 text-center hover:border-primary cursor-pointer transition-colors duration-300"
        @click="triggerFileInput" @dragover.prevent="dragover = true" @dragleave.prevent="dragover = false"
        @drop.prevent="onFileDrop" :class="{ 'bg-primary-lighter border-primary': dragover }">
        <input ref="fileInput" type="file" class="hidden" @change="onUploadFile" multiple />
        <i class="text-4xl text-gray-400 mb-2 fas fa-cloud-upload-alt"></i>
        <p class="text-gray-600 mb-1">Drag and drop files here or click to browse</p>
        <p class="text-xs text-gray-500">Supported file types: PDF, JPG, PNG, DOC, XLS, etc.</p>
      </div>

      <!-- Attachments section -->
      <div v-if="attachments.length > 0">
        <div class="flex justify-between items-center mb-4">
          <h4 class="text-xl font-medium">Attachments ({{ attachments.length }})</h4>
          <div class="flex items-center text-sm">
            <span class="text-gray-600 mr-2">Sort by:</span>
            <select v-model="sortBy" class="border rounded px-2 py-1 text-sm">
              <option value="date-desc">Newest first</option>
              <option value="date-asc">Oldest first</option>
              <option value="name">File name</option>
            </select>
          </div>
        </div>

        <!-- Grid layout for attachments -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div v-for="attachment in sortedAttachments" :key="attachment._id"
            class="border rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-300">

            <!-- Image preview for image files -->
            <div v-if="isImageFile(attachment.fileName)"
              class="h-32 bg-gray-100 flex items-center justify-center overflow-hidden">
              <img :src="`https://ribu-production.s3.ap-southeast-2.amazonaws.com/${attachment.s3Key}`"
                class="object-cover w-full h-full" alt="Preview" @error="handleImageError" />
            </div>

            <!-- Icon for non-image files -->
            <div v-else class="h-32 flex items-center justify-center" :class="getFileColorClass(attachment.fileName)">
              <i class="material-icons text-4xl">{{ getFileIcon(attachment.fileName) }}</i>
            </div>

            <!-- File info -->
            <div class="p-3 bg-white">
              <div class="text-sm font-medium text-gray-800 truncate mb-1" :title="attachment.fileName">
                {{ attachment.fileName }}
              </div>
              <div class="text-xs text-gray-500 mb-3">
                {{ formatDate(attachment.createdAt) }}
              </div>

              <!-- Actions -->
              <div class="flex justify-between items-center">
                <span class="text-xs text-gray-500">
                  {{ getFileSize(attachment) }}
                </span>
                <div class="flex gap-2">
                  <!-- Preview / Download -->
                  <vs-button size="small" icon-pack="feather"
                    :icon="isImageFile(attachment.fileName) ? 'icon-eye' : 'icon-download'"
                    @click="downloadFile(attachment)">
                    {{ isImageFile(attachment.fileName) ? 'Preview' : 'Download' }}
                  </vs-button>

                  <!-- Remove Button -->
                  <vs-button size="small" color="danger" icon-pack="feather" icon="icon-trash"
                    @click="confirmRemoveAttachment(attachment)">
                    Remove
                  </vs-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="text-center py-8 bg-gray-50 rounded-lg">
        <i class="material-icons text-gray-400 text-4xl mb-2">folder_open</i>
        <p class="text-gray-500">No attachments found</p>
      </div>
    </vx-card>
    <vs-popup fullscreen title="Image Preview" :active.sync="previewVisible" :buttons-hidden="true"
      class="assessment-attachments-preview">
      <div class="flex justify-center items-center">
        <img :src="previewImage" alt="Preview" class="max-w-full max-h-screen rounded-lg shadow-lg" />
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { RepositoryFactory } from '@/repository/RepositoryFactory'
const ActionRepository = RepositoryFactory.get('action');

export default {
  name: "ActionAttachments",
  data() {
    return {
      object_name: 'action',
      object_id: this.$route.params.id,
      attachments: [],
      dragover: false,
      sortBy: 'date-desc',
      selectedFile: null,
      placeholderImgs: {}, // Track images that failed to load
      previewVisible: false,
      previewImage: null
    }
  },
  computed: {
    sortedAttachments() {
      return [...this.attachments].sort((a, b) => {
        if (this.sortBy === 'date-desc') {
          return new Date(b.createdAt) - new Date(a.createdAt);
        } else if (this.sortBy === 'date-asc') {
          return new Date(a.createdAt) - new Date(b.createdAt);
        } else if (this.sortBy === 'name') {
          return a.fileName.localeCompare(b.fileName);
        }
        return 0;
      });
    }
  },
  async mounted() {
    await this.fetchAttachments();
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    async onUploadFile(event) {
      const files = Array.from(event.target.files);
      if (!files.length) return;

      this.$vs.loading();

      try {
        const formData = new FormData();

        files.forEach(file => {
          formData.append('files', file);
        });

        await ActionRepository.uploadAttachment(
          this.object_name,
          this.object_id,
          formData
        );

        await this.fetchAttachments();
      } catch (e) {
        console.log(e);
      }

      this.$vs.loading.close();
      event.target.value = '';
    },
    onFileDrop(event) {
      this.dragover = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.$refs.fileInput.files = files;
        this.onUploadFile({ target: this.$refs.fileInput });
      }
    },
    async fetchAttachments() {
      this.$vs.loading();
      try {
        const { data } = (await ActionRepository.getActionAttachments(this.object_name, this.object_id)).data;
        this.attachments = data;
      } catch (e) {
        console.log(e);
      }
      this.$vs.loading.close();
    },
    isImageFile(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(ext);
    },
    handleImageError(event) {
      // Replace broken image with the appropriate file icon
      const target = event.target;
      const parent = target.parentElement;

      // Find the attachment by searching for the img src
      const s3KeyPath = target.src.split('amazonaws.com/')[1];
      const attachment = this.attachments.find(a => a.s3Key === s3KeyPath);

      if (attachment && parent) {
        // Add the attachment to failed images
        this.$set(this.placeholderImgs, attachment._id, true);

        // Force a re-render
        this.$forceUpdate();
      }
    },
    getFileIcon(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      switch (ext) {
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          return 'image';
        case 'pdf':
          return 'picture_as_pdf';
        case 'doc':
        case 'docx':
          return 'description';
        case 'xls':
        case 'xlsx':
          return 'table_chart';
        case 'zip':
        case 'rar':
          return 'archive';
        default:
          return 'insert_drive_file';
      }
    },
    getFileColorClass(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();

      if (['pdf'].includes(ext)) return 'bg-red-500';
      if (['doc', 'docx'].includes(ext)) return 'bg-blue-500';
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'bg-green-500';
      if (['ppt', 'pptx'].includes(ext)) return 'bg-orange-500';
      if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext)) return 'bg-purple-500';
      if (['zip', 'rar', '7z'].includes(ext)) return 'bg-yellow-500';
      if (['mp4', 'avi', 'mov'].includes(ext)) return 'bg-pink-500';

      return 'bg-gray-500';
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    getFileSize(attachment) {
      // If your API returns file size, you can format it here
      // Example: if attachment.fileSize exists
      if (attachment.fileSize) {
        const kb = attachment.fileSize / 1024;
        if (kb < 1024) {
          return `${Math.round(kb)} KB`;
        } else {
          return `${(kb / 1024).toFixed(1)} MB`;
        }
      }
      return '';
    },
    downloadFile(attachment) {
      if (!attachment.s3Key || !attachment.fileName) {
        console.error('Missing S3 key or file name.');
        return;
      }
      const fileUrl = `https://ribu-production.s3.ap-southeast-2.amazonaws.com/${attachment.s3Key}`;
      if (this.isImageFile(attachment.fileName)) {
        // Open preview modal
        this.previewImage = fileUrl;
        this.previewVisible = true;
      } else {
        // Non-image files → just download
        const newTab = window.open(fileUrl, '_blank');
        if (!newTab) {
          console.error('Popup blocked');
        }
      }
    },
    async confirmRemoveAttachment(attachment) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Remove Attachment',
        acceptText: 'Confirm',
        text: `Are you sure you want to remove "${attachment.fileName}"?`,
        accept: () => this.removeAttachment(attachment),
      });
    },
    async removeAttachment(attachment) {
      await ActionRepository.deleteActionAttachment(this.object_name, this.object_id, attachment._id)
      await this.fetchAttachments()
    },
  }
}
</script>